import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FormFields,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import {
  UserResetMFADialogWithButton,
  UserSendPasswordResetEmailDialogWithButton,
  UserUpdatePasswordDialogWithButton,
} from '@product-framework/components/Dialogs';
import { editSASchema, editAdminSchema } from '../../schemas';
import validationSchema from './validations';

function UserEdit() {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();
  const userAccess = useUserAccess();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/users/${pageId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/users/${pageId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);
  const preProcessRequestData = data => {
    const { enabled, companyId, smsSubscriptionStatus, id, role, ...other } =
      data;
    return {
      enabled: enabled === 'true',
      userRoleId: role?.value,
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { role, ...other } = data;
    return {
      ...other,
      role: {
        label: role?.roleIdentifier,
        value: role?.id,
      },
    };
  };
  const getSchema = () => {
    if (userAccess.isAdmin()) {
      return editAdminSchema;
    }
    return editSASchema;
  };

  const submitEditForm = async formData => {
    const { disabledReason, ...data } = formData;
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_UPDATE_SUCCESS',
          defaultMessage: 'User was saved successfully',
        }),
      );
      setInitialValues(preProcessFetchData(response?.data));
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'USER_UPDATE_FAILURE',
          defaultMessage: 'Error: Could not save the user',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    setFormErrors(null);
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(response?.data),
      });
      setLoading(false);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'USER_DATA_FETCH_FAILURE',
          defaultMessage: 'Error: Could not fetch the user data',
        }),
      });
    }
  };

  useEffect(() => {
    fetchInitialData(pageId);
  }, [pageId]);

  const formProps = {
    customComponents: FormFields,
    schema: getSchema(),
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
    permissionEntity: userAccess.isAdmin()
      ? 'ADMINUSER'
      : 'SERVICEADMINISTRATORUSER',
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'USERS_BREADCRUMB',
        defaultMessage: 'Users',
      }),
      icon: IconLibrary.Users,
      onClick: () => {
        navigate('/users');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_USER_EDIT_BREADCRUMB',
        defaultMessage: 'Edit User',
      }),
      icon: IconLibrary.User,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_USERS_STACKABLE_BUTTON',
        defaultMessage: 'Create User',
      }),
      Icon: IconLibrary.User,
      permissionName: 'CAN_CREATE_USER',
      variant: 'outlined',
      onClick: () => navigate('/users/create'),
    },
    {
      label: formatMessage({
        id: 'EDIT_USERS_STACKABLE_BUTTON',
        defaultMessage: 'List Users',
      }),
      Icon: IconLibrary.Users,
      variant: 'outlined',
      onClick: () => navigate('/users'),
    },
  ];

  return (
    <Layout>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing..." />
        </LoadingEvent>
      )}

      {!loading && (
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
      )}

      {!loading && (
        <PageSection
          label={formatMessage({
            id: 'USER_EDIT_HEADING',
            defaultMessage: 'Edit User',
          })}>
          <Box>
            <CRUDComponent
              errors={formErrors}
              type={CRUD_COMPONENT_TYPE.FORM}
              formProps={formProps}>
              <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
                <PermissionButton
                  permissionName="CAN_CREATE_EDIT_USER"
                  variant="contained"
                  startIcon={<IconLibrary.Save />}
                  color="primary"
                  onClick={() => editFormRef.current?.submit()}>
                  {formatMessage({
                    id: 'USER_UPDATE_SAVE_BUTTON',
                    defaultMessage: 'Save User',
                  })}
                </PermissionButton>
                <PermissionButton
                  permissionName="CAN_CREATE_EDIT_USER"
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => editFormRef.current?.reset()}>
                  {formatMessage({
                    id: 'USER_UPDATE_RESET_BUTTON',
                    defaultMessage: 'Reset',
                  })}
                </PermissionButton>
                <UserUpdatePasswordDialogWithButton
                  email={initialValues?.email}
                  firstName={initialValues?.firstName}
                  lastName={initialValues?.lastName}
                  permissionName="CAN_UPDATE_SA_PASSWORD"
                />
                <UserResetMFADialogWithButton
                  email={initialValues?.email}
                  firstName={initialValues?.firstName}
                  lastName={initialValues?.lastName}
                  permissionName="CAN_RESET_SA_USER_MFA"
                />
                <UserSendPasswordResetEmailDialogWithButton
                  email={initialValues?.email}
                  firstName={initialValues?.firstName}
                  lastName={initialValues?.lastName}
                  permissionName="CAN_SEND_SA_RESET_PASSWORD_EMAIL"
                />
              </Box>
            </CRUDComponent>
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(UserEdit);
